import Slider from '@react-native-community/slider'
import { useAudioPlayer, useAudioPlayerStatus } from 'expo-audio'
import { useEffect, useState, type ReactNode } from 'react'
import { Platform, View } from 'react-native'

import { Message } from '@/src/elements/message/Message'
import { useTranslate } from '@/src/lang/useTranslate'
import { colors } from '@/src/styles/colors'

export type TAudioPlayerProps = {
  source: string
  shouldPlay: boolean
  onFinished?: VoidFunction
}

export const AudioPlayer = ({ source, shouldPlay, onFinished }: TAudioPlayerProps): ReactNode => {
  const [isFocused, setIsFocused] = useState(false)
  const [isUserInteracting, setIsUserInteracting] = useState(false)
  const [userPosition, setUserPosition] = useState(0)
  const [isErrored, setIsErrored] = useState(false)
  const player = useAudioPlayer({ uri: source })
  const status = useAudioPlayerStatus(player)

  const { t } = useTranslate()

  useEffect(() => {
    let playPromise: void | Promise<void>

    try {
      if (shouldPlay) {
        // Web API exposes a Promise, the promise is ignored by expo-audio
        // but must be handled on web, see : https://goo.gl/LdLk22
        // This is why expo-audio has been patched on web
        playPromise = player.play()
      } else {
        player.pause()
        setIsErrored(false)
        return
      }
    } catch {
      setIsErrored(true)
      return
    }

    if (playPromise) {
      playPromise
        .then(() => setIsErrored(false))
        .catch((e: Error) => {
          if (e.name !== 'AbortError') {
            setIsErrored(true)
          }
        })
    } else {
      setIsErrored(false)
    }
  }, [shouldPlay, player])

  useEffect(() => {
    if (status.didJustFinish) {
      onFinished?.()
    }
  }, [status.didJustFinish, onFinished])

  const hasLoadingIssue = !status.isLoaded && !status.isBuffering && status.duration === 0
  const hasPlaybackIssue =
    status.reasonForWaitingToPlay?.includes('error') || status.timeControlStatus === 'waitingWithNoItemToPlay'

  // Calculate position (0-1 ratio)
  const position = status.duration > 0 ? status.currentTime / status.duration : 0

  const additionalProps = Platform.select({
    native: {},
    web: {
      dataSet: { 'focus-visible': isFocused },
      onFocus: () => setIsFocused(true),
      onBlur: () => setIsFocused(false),
      className:
        'flex flex-row items-center justify-between rounded-md px-1 outline-none web:data-[focus-visible=true]:outline-4 web:data-[focus-visible=true]:outline-offset-1 web:data-[focus-visible=true]:outline-layout-focus',
      onMouseDown: (e: MouseEvent) => e.preventDefault(),
    },
  })

  return isErrored || hasLoadingIssue || hasPlaybackIssue ? (
    <Message colors="danger">{t('app.audio.loading-error')}</Message>
  ) : (
    <View tabIndex={0} {...additionalProps}>
      <Slider
        className="w-full"
        minimumValue={0}
        maximumValue={1}
        value={isUserInteracting ? userPosition : position}
        minimumTrackTintColor={colors.primary['text-contrast-low'].DEFAULT}
        maximumTrackTintColor={colors.primary['bg-high'].DEFAULT}
        thumbTintColor={colors.primary['text-contrast-low'].DEFAULT}
        onSlidingStart={() => {
          setIsUserInteracting(true)
          setUserPosition(position)
        }}
        onValueChange={(value) => {
          if (isUserInteracting) {
            setUserPosition(value)
          }
        }}
        onSlidingComplete={(finalPosition) => {
          player.seekTo(finalPosition * status.duration)
          setIsUserInteracting(false)
        }}
      />
    </View>
  )
}
